import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  computed,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatCurrencyVND, formatDateTime } from '@app/shared/utils/date-formatter.util';

import { 
  PosShiftCloseService, 
  PosShiftClosing,
  PosInvoice,
  SalesSummary,
  PaymentMethod,
  PaymentReconciliation
} from '../services/pos-shift-close.service';
import { AddFormComponent, FormField, ActionButton } from "@app/shared/components/add-form/add-form.component";
import { InputNumberModule } from 'primeng/inputnumber';
import { FormsModule } from '@angular/forms';
import { 
    POS_SHIFT_CLOSE_INVOICE_COLUMNS,
    POS_SHIFT_CLOSE_PAYMENT_METHODS_COLUMNS,
    POS_SHIFT_CLOSE_PAYMENT_RECONCILIATION_COLUMNS,
    PosShiftCloseFormConfig,
    getPosShiftCloseFormConfig
  } from '../pos-shift-close.config';
import { TableColumn, DataTableComponent } from '@app/shared/components/data-table/data-table.component';

@Component({
  selector: 'app-pos-shift-close-view',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    TranslateModule,
    AddFormComponent,
    DataTableComponent,
    InputNumberModule,
    FormsModule
  ],
  providers: [MessageService],
  templateUrl: './pos-shift-close-view.component.html',
  styleUrls: ['./pos-shift-close-view.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PosShiftCloseViewComponent implements OnInit {
  private posShiftCloseService = inject(PosShiftCloseService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  
  // Data signals
  shiftClosing = signal<PosShiftClosing | null>(null);
  loading = signal(true);
  
  // Section data signals
  linkedInvoices = signal<PosInvoice[]>([]);
  salesSummary = signal<SalesSummary | null>(null);
  paymentMethods = signal<PaymentMethod[]>([]);
  paymentReconciliation = signal<PaymentReconciliation[]>([]);

  // Computed properties
  totalAmount = computed(() => this.salesSummary()?.totalAmount || 0);
  totalNetAmount = computed(() => this.salesSummary()?.totalNetAmount || 0);
  totalQuantity = computed(() => this.salesSummary()?.totalQuantity || 0);

  // Form configuration
  config!: PosShiftCloseFormConfig;
  POS_SHIFT_CLOSE_INVOICES_COLUMNS: TableColumn[] = POS_SHIFT_CLOSE_INVOICE_COLUMNS;
  POS_SHIFT_CLOSE_PAYMENT_METHODS_COLUMNS: TableColumn[] = POS_SHIFT_CLOSE_PAYMENT_METHODS_COLUMNS;
  POS_SHIFT_CLOSE_PAYMENT_RECONCILIATION_COLUMNS: TableColumn[] = POS_SHIFT_CLOSE_PAYMENT_RECONCILIATION_COLUMNS;

  ngOnInit(): void {
    // Initialize form configuration
    this.config = getPosShiftCloseFormConfig(this.posShiftCloseService);
    
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadShiftClosingDetail(parseInt(id, 10));
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('pos.shift_close.messages.not_found'),
      });
      this.router.navigate(['/pos/shift-close']);
    }
  }

  /**
   * Get dynamic summary items with current values
   */
  getSummaryItems() {
    return [
      { 
        label: 'pos.shift_close.detail.table.summary.total_quantity', 
        prop: 'totalQuantity', 
        value: this.totalQuantity(), 
        formatter: 'number' 
      },
      { 
        label: 'pos.shift_close.detail.table.summary.total', 
        prop: 'totalAmount', 
        value: this.totalAmount(), 
        formatter: 'currency', 
        currency: 'VND' 
      },
      { 
        label: 'pos.shift_close.detail.table.summary.total_net', 
        prop: 'totalNetAmount', 
        value: this.totalNetAmount(), 
        formatter: 'currency', 
        currency: 'VND' 
      }
    ];
  }

  /**
   * Get data table sections configuration
   */
  getDataTableSections() {
    return [
      {
        title: 'pos.shift_close.detail.sections.pos_transactions',
        data: this.linkedInvoices(),
        columns: this.POS_SHIFT_CLOSE_INVOICES_COLUMNS
      },
      {
        title: 'pos.shift_close.detail.sections.payment_methods',
        data: this.paymentMethods(),
        columns: this.POS_SHIFT_CLOSE_PAYMENT_METHODS_COLUMNS
      },
      {
        title: 'pos.shift_close.detail.sections.payment_reconciliation',
        data: this.paymentReconciliation(),
        columns: this.POS_SHIFT_CLOSE_PAYMENT_RECONCILIATION_COLUMNS
      }
    ];
  }

  /**
   * Load shift closing detail data
   */
  private loadShiftClosingDetail(id: number): void {
    this.loading.set(true);
    
    this.posShiftCloseService.getPosShiftClosingById(id).subscribe({
      next: (data) => {
        // Format datetime fields for display
        const formattedData = {
          ...data,
          openingTime: this.formatDateTime(data.openingTime),
          closingTime: this.formatDateTime(data.closingTime)
        };
        
        this.shiftClosing.set(formattedData);
        
        // Load additional sections
        if (data.linkedInvoices) {
          this.linkedInvoices.set(data.linkedInvoices);
        }
        if (data.salesSummary) {
          this.salesSummary.set(data.salesSummary);
        }
        if (data.paymentMethods) {
          this.paymentMethods.set(data.paymentMethods);
        }
        if (data.paymentReconciliation) {
          this.paymentReconciliation.set(data.paymentReconciliation);
        }
        
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading shift closing detail:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant('pos.shift_close.messages.load_failed'),
        });
        this.router.navigate(['/pos/shift-close']);
      }
    });
  }

  /**
   * Handle invoice selection change
   */
  onInvoiceSelectionChange(invoice: PosInvoice, event: Event): void {
    const target = event.target as HTMLInputElement;
    const selected = target?.checked || false;
    this.onInvoiceSelection(invoice, selected);
  }

  /**
   * Handle invoice selection
   */
  onInvoiceSelection(invoice: PosInvoice, selected: boolean): void {
    const invoices = this.linkedInvoices();
    const index = invoices.findIndex(inv => inv.id === invoice.id);
    if (index !== -1) {
      invoices[index].isSelected = selected;
      this.linkedInvoices.set([...invoices]);
    }
  }

  /**
   * Handle payment reconciliation selection change
   */
  onPaymentReconciliationSelectionChange(payment: PaymentReconciliation, event: Event): void {
    const target = event.target as HTMLInputElement;
    const selected = target?.checked || false;
    this.onPaymentReconciliationSelection(payment, selected);
  }

  /**
   * Handle payment reconciliation selection
   */
  onPaymentReconciliationSelection(payment: PaymentReconciliation, selected: boolean): void {
    const payments = this.paymentReconciliation();
    const index = payments.findIndex(p => p.id === payment.id);
    if (index !== -1) {
      payments[index].isSelected = selected;
      this.paymentReconciliation.set([...payments]);
    }
  }

  /**
   * Navigate back to shift close list
   */
  onBack(): void {
    this.router.navigate(['/pos/shift-close']);
  }

  /**
   * Format currency using utility function
   */
  formatCurrency = formatCurrencyVND;

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  /**
   * Handle form value changes
   */
  onFormValueChanges(value: any): void {
    // No need to handle form changes in view mode
  }

  /**
   * Handle form actions
   */
  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'back':
        this.router.navigate(['/pos/shift-close']);
        break;
      default:
    }
  }
}
